import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useOrganizationDetails } from "hooks/api/fulfillment/organization";
import { useRolesIndex, useRoleDetails } from "hooks/api/common/roles";
import useUser from "hooks/reduxContainer/_common/useUser";
import Permissions from "lib/regular/permissions";
import AssignPermissionsForm from "components/_common/organization/AssignPermissionsForm";
import { get, uniqBy } from "lodash-es";
import { Input, Button, Alert, notification, Divider, Select } from "antd";
import FulfillmentBackButton from "../../../fulfillment/FulfillmentBackButton";
import Loader from "components/Loader/Loader";
import Organization from "lib/organization";
import { OPS_ONLY } from "config/roleConfig";

const applicationAccessOptions = [
  { value: "sierra", label: "Admin Portal (Sierra)" },
  { value: "portal", label: "Merchant Portal (Business)" },
  { value: "providence", label: "Warehouse Portal (Providence)" },
  { value: "bvr", label: "Routing Portal (BVR)" },
  { value: "goose", label: "Mobile Pick App (Goose)" },
];

const OrganizationUserRolesCreateEditPage = (props) => {
  const [roleName, setRoleName] = useState("");
  const [currentRoleName, setCurrentRoleName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [inputError, setInputError] = useState("");
  const [selectedApplications, setSelectedApplications] = useState([]);

  const { organizationDetails, fetchOrganizationDetails } = useOrganizationDetails();
  const { fetchRolesIndex, rolesIndex } = useRolesIndex();
  const { fetchRoleDetails, createRole, updateRole, roleDetails } = useRoleDetails();
  const { user } = useUser();

  const history = useHistory();
  const params = useParams();

  const organizationID = get(params, "organization_id");
  const roleID = get(params, "role_id", "");
  const isCreatePage = !roleID;
  const query = { organization_id: organizationID };

  const prefillForm = async () => {
    if (isCreatePage) return;
    const role = await fetchRoleDetails(roleID);
    if (role) {
      const name = get(role, "name");
      setRoleName(name);
      setCurrentRoleName(getTrimmedLowerCase(name));

      const rolePermissions = get(role, "permissions");
      const permissionIDs = Permissions.getPermissionIdsArray(rolePermissions);
      setSelectedPermissions(permissionIDs);

      setSelectedApplications(get(role, "applications", []));
    }
  };

  // eslint-disable-next-line
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchOrganizationDetails(organizationID);
    fetchRolesIndex(query);
    prefillForm();
  }, []);

  useEffect(() => {
    setInputError("");
  }, [roleName, selectedPermissions]);

  const error = organizationDetails.error || rolesIndex.error;

  useEffect(() => {
    if (error) {
      notification.error({
        message: "Error",
        description: error,
      });
    }
  }, [error]);

  const loading = organizationDetails.loading || rolesIndex.loading || roleDetails.loading;
  if (loading) return <Loader />;

  const renderHeader = () => {
    const organizationName = get(organizationDetails, "data.name", "");

    return (
      <div className="roles-create-header">
        <FulfillmentBackButton urlPath={`/organizations/${organizationID}/roles`} label="User Roles" />
        <div className="page-title">{roleID ? "Edit" : "Create"} User Role</div>
        <div className="organization-name">{organizationName}</div>
      </div>
    );
  };

  const renderNameInput = () => {
    return (
      <div className="name-input">
        <h3 className="header">ROLE NAME</h3>
        <Input placeholder="Role Name" value={roleName} onChange={(e) => setRoleName(e.target.value)} />
        <Divider />
        <h3 className="header">Application Access</h3>
        <Select
          mode="multiple"
          style={{ width: "100%" }}
          options={applicationAccessOptions}
          onChange={setSelectedApplications}
          defaultValue={selectedApplications}
          allowClear
        />
      </div>
    );
  };

  const getTrimmedLowerCase = (str) => str.trim().toLowerCase();

  const validateForm = () => {
    if (selectedPermissions.length < 1) {
      setInputError("Please select at least one permission for this role.");
      return false;
    }

    if (roleName.trim().length < 1) {
      setInputError("Role name must not be empty.");
      return false;
    }
    const isRoleNameTaken = !!rolesIndex.data.find(
      (role) => getTrimmedLowerCase(roleName) === getTrimmedLowerCase(role.name)
    );

    if (isRoleNameTaken && getTrimmedLowerCase(roleName) !== currentRoleName) {
      setInputError(`Role "${roleName.trim()}" already exists.`);
      return false;
    }
    return true;
  };

  const createNewRole = () => {
    const body = {
      role: {
        name: roleName,
        organization_id: organizationID,
        permission_ids: selectedPermissions,
      },
    };
    return createRole(body);
  };

  const updateExistingRole = () => {
    const body = {
      role: {
        name: roleName,
        permission_ids: selectedPermissions,
        applications: selectedApplications,
      },
    };
    return updateRole(roleID, body);
  };

  const onFormSubmit = async () => {
    const formIsValid = validateForm();

    if (formIsValid) {
      if (!isCreatePage) {
        const updatedRole = await updateExistingRole();

        if (updatedRole) {
          history.push(`/organizations/${organizationID}/roles`);
        }
      } else {
        const newRole = await createNewRole();

        if (newRole) {
          history.push(`/organizations/${organizationID}/roles`);
        }
      }
    }
  };

  const renderAlertBanner = () => {
    if (inputError) {
      return <Alert message={inputError} type="error" banner />;
    }
  };

  const organizationPermissions = get(organizationDetails.data, "permissions", []);

  const uniqueOrganizationPermissions = uniqBy(organizationPermissions, "name");

  return (
    <div id="org-user-role-create" className="page-container">
      {renderHeader()}
      {renderAlertBanner()}
      <div className="role-create-form">
        {renderNameInput()}
        <AssignPermissionsForm
          selectedPermissions={selectedPermissions}
          setSelectedPermissions={setSelectedPermissions}
          availablePermissions={uniqueOrganizationPermissions}
          isSecondCloset={Organization.isSecondCloset(organizationDetails.data)}
        />
      </div>
      <Button
        className="role-submit-btn"
        disabled={user.subrole && OPS_ONLY.includes(user.subrole)}
        onClick={onFormSubmit}
      >
        Submit
      </Button>
    </div>
  );
};

export default OrganizationUserRolesCreateEditPage;
